/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { getHeap, useTrackView } from "../../../../utils/heap"
import { emailValidationOptionalCarotene } from "../../../../utils/Regexes"
import { isHttpError } from "../../../../utils/HttpErrors"
import { CarrotErrorCodes } from "../../../../utils/CarrotErrors"
import CarrotClient from "carrot-api"
import Settings from "../../../../utils/CarrotConfig"
import { reportError } from "utils/ErrorReporting"
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { Form, Body, FormInput } from "@carrotfertility/carotene"
import { Spacer } from "components/views/atoms/Atoms"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { Box, Stack, useTheme } from "@carrotfertility/carotene-core"
import { RegistrationButtonFooter, SkipButton, ContinueButton } from "./RegistrationButtons"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

interface PersonalEmailProps {
  personalEmail?: string
  email: string
  setPersonalEmail: (newValue: string) => void
  onComplete: () => void
  onSkip: () => void
  onBack: () => void
}

export default function PersonalEmail({
  personalEmail,
  email,
  onComplete,
  onSkip,
  onBack,
  setPersonalEmail
}: PersonalEmailProps): JSX.Element {
  useTrackView("PersonalEmailRegFlow", "Personal email screen")
  const intl = useIntl()
  const theme = useTheme()

  function validateEmailIsAbleToBeUsed(personalEmail): string | boolean | MessageDescriptor {
    const sanitizedPersonalEmail = personalEmail.toLowerCase()
    if (sanitizedPersonalEmail === email) {
      return intl.formatMessage({
        defaultMessage: "This is your account address. Either provide a different address or skip this step."
      })
    }
    return emailValidationOptionalCarotene(personalEmail)
  }

  async function onCompleteSetPersonalEmail(formValues, instance): Promise<void> {
    const sanitizedPersonalEmail = formValues?.personalEmail.toLowerCase()
    if (formValues?.personalEmail) {
      try {
        await client.checkEmailUniqueness(sanitizedPersonalEmail)
      } catch (err) {
        if (isHttpError(err)) {
          const { code } = await err.response.json()
          if (code === CarrotErrorCodes.PERSONAL_EMAIL_ALREADY_EXISTS) {
            instance.setFieldMeta("personalEmail", {
              error: intl.formatMessage({ defaultMessage: "That email address is already in use" })
            })
            getHeap().track("DuplicateEmail", {
              EventName: "Attempted to use non-unique email"
            })
            return
          }
        } else {
          instance.setFieldMeta("personalEmail", {
            error: intl.formatMessage({ defaultMessage: "Oops, something went wrong" })
          })
          reportError(err)
          return
        }
      }
      setPersonalEmail(formValues?.personalEmail)
      onComplete()
    } else {
      onSkip()
    }
  }

  return (
    <RegistrationFlowLayout
      header={<FormattedMessage defaultMessage="Should we contact you at a different email address?" />}
      description={
        <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          <Body color="black-80">
            <FormattedMessage
              defaultMessage="The address you use to sign in won't change, but if you'd like us to
       communicate with you at a different address, share that here.
       You can also update this later in your account settings."
            />
          </Body>
          <Spacer height={2.5} />
        </Box>
      }
      interactiveElements={
        <Form onSubmit={onCompleteSetPersonalEmail}>
          <Stack spacing={theme.spacing(theme.tokens.spacing.xl)}>
            <FormInput
              label={intl.formatMessage({ defaultMessage: "Contact email address" })}
              name={"personalEmail"}
              defaultValue={personalEmail}
              validate={validateEmailIsAbleToBeUsed}
            />
            <RegistrationButtonFooter
              onBack={onBack}
              skipButton={
                <SkipButton onClick={onSkip}>
                  <FormattedMessage defaultMessage="Skip" />
                </SkipButton>
              }
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Stack>
        </Form>
      }
    />
  )
}
